import React, { useEffect, useState } from "react";
import { GetAuth } from "../../../../contexts/AuthProvider";
import image from "../../../../assets/images/vbg-login.jpg";
import {
  AlertBanner,
  Box,
  Button,
  InputField,
  Loader,
  Typography,
} from "@saviynt/design-system";
import "@saviynt/tokens";
import "./LoginUser.css";
import SavContainer from "../../../common/SavContainer/SavContainer";
import Unseal from "../../Unseal/Unseal";

function LoginUser() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [alert, setAlert] = useState(null);
  const [waitingForUnseal, setWaitingForUnseal] = useState(false);
  const [pendingCode, setPendingCode] = useState(null);
  const [verifiedCount, setVerifiedCount] = useState(null);
  const auth = GetAuth();

  // Poll token-exchange after custodian verified — auto-login when vault unseals
  useEffect(() => {
    if (!waitingForUnseal || !pendingCode) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/v1/bg/token-exchange", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: pendingCode }),
        });
        const data = await res.json();

        if (data.token) {
          // Got JWT — store token and cookies, then redirect to secrets
          clearInterval(interval);
          setWaitingForUnseal(false);
          setPendingCode(null);
          localStorage.setItem("site", data.token);
          document.cookie = "token_type=Bearer; path=/";
          document.cookie = `access_token=${data.token}; path=/`;
          window.location.reload();
        } else if (data.unsealed !== undefined) {
          // Update shard count from seal status
          setVerifiedCount((prev) => prev ? { ...prev, verified: data.shardCount || prev.verified } : prev);
        }
      } catch (e) {
        // silently retry
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [waitingForUnseal, pendingCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    setIsLoading(true);

    const result = await auth.loginAction({ username, password });

    if (result.success) {
      if (result.token) {
        // Got JWT — MainView will redirect to Home
      } else {
        // Custodian verified but vault not yet unsealed — poll for auto-login
        setAlert({
          colorTheme: "Success",
          title: "Verified",
          description: `Custodian verified (${result.verified}/${result.required}). Waiting for remaining custodians...`,
        });
        setUsername("");
        setPassword("");
        setVerifiedCount({ verified: result.verified, required: result.required });
        setPendingCode(result.pendingCode);
        setWaitingForUnseal(true);
      }
    } else {
      setAlert({
        colorTheme: "Critical",
        title: "Authentication Failed",
        description: result.error || "Unexpected error occurred during login.",
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    let passwordTimeout;
    if (isPasswordVisible) {
      passwordTimeout = setTimeout(() => {
        setIsPasswordVisible(false);
      }, 3000);
    }
    return () => clearTimeout(passwordTimeout);
  }, [isPasswordVisible]);

  const handleAlertDismiss = () => {
    setAlert(null);
  };

  // Show Unseal progress screen when waiting for other custodians
  if (waitingForUnseal && verifiedCount) {
    return (
      <>
        {alert && (
          <AlertBanner
            colorTheme={alert.colorTheme}
            title={alert.title}
            description={alert.description}
            isVisible={true}
            onCancel={handleAlertDismiss}
            isColonVisible={false}
            isFloating
            position={{ type: "fixed", top: "4.25rem" }}
            shouldAutoDismiss
            autoDismissDelay={5000}
          />
        )}
        <Unseal
          shardCount={verifiedCount.verified}
          requiredShards={verifiedCount.required}
        />
      </>
    );
  }

  return (
    <SavContainer>
      {alert && (
        <AlertBanner
          colorTheme={alert.colorTheme}
          title={alert.title}
          description={alert.description}
          isVisible={true}
          onCancel={handleAlertDismiss}
          isColonVisible={false}
          isFloating
          position={{ type: "fixed", top: "4.25rem" }}
          shouldAutoDismiss
          autoDismissDelay={5000}
        />
      )}
      <img src={image} alt="bg1" />
      <Box className="LoginUser">
        <Box className="LoginUser-form">
          <Box className="LoginUser-header">
            <Typography kind="h1">Saviynt Vault Break Glass</Typography>
          </Box>
          {isLoading ? (
            <Box className="LoginUser-loader">
              <Loader kind="dots" format="inline" color="info" size="medium" />
            </Box>
          ) : (
            <Box className="LoginUser-formContent">
              <InputField
                type="text"
                name="email"
                placeholder="Email"
                value={username}
                setValue={setUsername}
                label={<Typography kind="label">Email:</Typography>}
                classNames={{
                  inputContainer: "LoginUser-inputContainer",
                  labelContainer: "LoginUser-labelContainer",
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
                isRequired
              />
              <InputField
                type={isPasswordVisible ? "text" : "password"}
                name="password"
                value={password}
                setValue={setPassword}
                placeholder="Key Share"
                label={<Typography kind="label">Key Share:</Typography>}
                classNames={{
                  inputContainer: "LoginUser-inputContainer",
                  labelContainer: "LoginUser-labelContainer",
                }}
                suffixButtonType="password"
                suffixIsToggle={isPasswordVisible}
                suffixOnClick={() => setIsPasswordVisible(!isPasswordVisible)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
                isRequired
              />
              <Box className="LoginUser-submitButton">
                <Button
                  type="button"
                  kind="filled"
                  className="LoginUser-signIn"
                  onClick={handleSubmit}
                  isDisabled={username === "" || password === "" || isLoading}
                >
                  {isLoading ? "Signing In..." : "Sign In"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </SavContainer>
  );
}

export default LoginUser;
